/**
 * Comment Item Component
 * ======================
 * Displays a single comment with actions and nested replies.
 */

import { useState } from 'react'
import { commentService, type CommentWithUser } from '../../services'
import { CommentForm } from './CommentForm'

interface CommentItemProps {
  comment: CommentWithUser
  currentUserId?: string
  videoOwnerId?: string
  onReply: (parentId: string, content: string) => Promise<void>
  onEdit: (commentId: string, content: string) => Promise<void>
  onDelete: (commentId: string) => Promise<void>
  onHide?: (commentId: string) => Promise<void>
  depth?: number
}

export function CommentItem({
  comment,
  currentUserId,
  videoOwnerId,
  onReply,
  onEdit,
  onDelete,
  onHide,
  depth = 0,
}: CommentItemProps) {
  const [isReplying, setIsReplying] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [showActions, setShowActions] = useState(false)

  const isOwner = currentUserId === comment.user_id
  const isVideoOwner = currentUserId === videoOwnerId
  const canModerate = isVideoOwner && !isOwner
  const maxDepth = 3

  const handleReply = async (content: string) => {
    await onReply(comment.id, content)
    setIsReplying(false)
  }

  const handleEdit = async (content: string) => {
    await onEdit(comment.id, content)
    setIsEditing(false)
  }

  const handleDelete = async () => {
    if (confirm('Delete this comment?')) {
      await onDelete(comment.id)
    }
  }

  const handleHide = async () => {
    if (onHide && confirm('Hide this comment?')) {
      await onHide(comment.id)
    }
  }

  const userName = comment.user?.full_name || 'Anonymous'
  const avatarUrl = comment.user?.avatar_url
  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <div className={depth > 0 ? 'ml-12 border-l-2 border-gray-100 pl-4' : ''}>
      <div
        className="group py-3"
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        <div className="flex gap-3">
          {/* Avatar */}
          <div className="flex-shrink-0">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={userName}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                {initials}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 text-sm">
                {userName}
              </span>
              {isOwner && (
                <span className="text-xs text-gray-400">(you)</span>
              )}
              <span className="text-xs text-gray-400">
                {commentService.formatRelativeTime(comment.created_at)}
              </span>
              {comment.is_edited && (
                <span className="text-xs text-gray-400">(edited)</span>
              )}
            </div>

            {isEditing ? (
              <div className="mt-2">
                <CommentForm
                  initialContent={comment.content}
                  onSubmit={handleEdit}
                  onCancel={() => setIsEditing(false)}
                  submitLabel="Save"
                  autoFocus
                />
              </div>
            ) : (
              <p className="mt-1 text-gray-700 text-sm whitespace-pre-wrap break-words">
                {comment.content}
              </p>
            )}

            {/* Actions */}
            {!isEditing && (
              <div
                className={`mt-2 flex items-center gap-4 text-xs transition-opacity ${
                  showActions ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {currentUserId && depth < maxDepth && (
                  <button
                    onClick={() => setIsReplying(!isReplying)}
                    className="text-gray-500 hover:text-blue-600"
                  >
                    Reply
                  </button>
                )}
                {isOwner && (
                  <>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="text-gray-500 hover:text-blue-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={handleDelete}
                      className="text-gray-500 hover:text-red-600"
                    >
                      Delete
                    </button>
                  </>
                )}
                {canModerate && onHide && (
                  <button
                    onClick={handleHide}
                    className="text-gray-500 hover:text-orange-600"
                  >
                    Hide
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Reply form */}
        {isReplying && (
          <div className="mt-3 ml-11">
            <CommentForm
              onSubmit={handleReply}
              onCancel={() => setIsReplying(false)}
              placeholder={`Reply to ${userName}...`}
              submitLabel="Reply"
              isReply
              autoFocus
            />
          </div>
        )}
      </div>

      {/* Nested replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-1">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              currentUserId={currentUserId}
              videoOwnerId={videoOwnerId}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              onHide={onHide}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}
